import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Store,
  Users,
  TrendingUp,
  Truck,
  Briefcase,
  Check,
  Phone,
  Mail,
  MapPin,
  ChevronRight,
  MessageCircle,
  Send,
  Building2,
  Globe,
  Award,
  Target,
  Sparkles,
  ArrowRight,
  Menu,
  X,
} from "lucide-react";
import { Button } from "./components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "+919030636164";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_join-dravina/artifacts/6t4cf34q_dravina%20collective.png";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

// Opportunities data
const opportunities = [
  {
    id: "vendor",
    title: "Join as a Vendor / Seller",
    subtitle: "Promote and sell your products through our network",
    description: "If you are a manufacturer, brand owner, or supplier, Dravina provides a powerful distribution and marketing network to expand your reach.",
    icon: Store,
    color: "#2B7BB9",
    benefits: [
      "Access to our sales and distribution network",
      "Increased market visibility",
      "Support for marketing and promotions",
      "Faster market penetration",
    ],
    idealFor: ["Manufacturers", "FMCG brands", "Service providers", "Private label owners"],
    cta: "Become a Vendor – Apply Now",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "affiliate",
    title: "Affiliate Partners",
    subtitle: "Content Creators / Influencers / Publishers / Podcasters",
    description: "Earn by promoting Dravina products and services through your digital platforms.",
    icon: Users,
    color: "#4CAF50",
    benefits: [
      "Attractive affiliate commissions",
      "Unique tracking links",
      "Performance-based incentives",
      "Long-term earning opportunity",
    ],
    idealFor: ["YouTubers", "Bloggers", "Instagram / Social Media Influencers", "Podcasters", "Website owners"],
    cta: "Join as Affiliate",
    image: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "leadership",
    title: "Leadership Career Program",
    subtitle: "Marketing & Sales Team Opportunity",
    description: "Build a career with unlimited income potential and leadership growth.",
    icon: TrendingUp,
    color: "#FF9800",
    benefits: [
      "Professional training and mentorship",
      "Career growth in marketing and sales",
      "Performance-based earnings and incentives",
      "Opportunity to build and lead your own team",
      "Path to financial freedom and recognition",
    ],
    idealFor: ["Freshers", "Entrepreneurs", "Part-time or full-time seekers", "Individuals passionate about sales and leadership"],
    cta: "Join Leadership Program",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "logistics",
    title: "Distribution & Logistics Partner",
    subtitle: "Last-Mile Delivery / Stock Point / Service Center",
    description: "Become part of our logistics network and support product distribution in your area.",
    icon: Truck,
    color: "#8BC34A",
    benefits: [
      "Delivery service charges",
      "Sales-based commissions",
      "Long-term business association",
      "Area-based growth opportunity",
    ],
    idealFor: ["Stock Point / Warehouse Partner", "Service Point", "Delivery Partner", "Regional Distribution Partner"],
    cta: "Apply for Logistics Partnership",
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "careers",
    title: "Join Our Team (Careers)",
    subtitle: "We are always looking for talented and passionate professionals",
    description: "Grow with us and become part of a dynamic team building the future of partnerships in India.",
    icon: Briefcase,
    color: "#2B7BB9",
    benefits: [
      "Competitive compensation",
      "Growth opportunities",
      "Dynamic work environment",
      "Professional development",
    ],
    idealFor: ["Sales & Marketing", "Operations", "Customer Support", "Logistics", "IT & Administration"],
    cta: "View Open Positions",
    image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=800&q=80",
  },
];

// Application Form Component
const ApplicationForm = ({ opportunity, isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    company_name: "",
    city: "",
    state: "",
    experience: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        application_type: opportunity.id,
      };

      await axios.post(`${API}/applications`, payload);
      toast.success("Application submitted successfully!", {
        description: "Our team will contact you soon.",
      });
      onClose();
      setFormData({
        full_name: "",
        email: "",
        phone: "",
        company_name: "",
        city: "",
        state: "",
        experience: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to submit application", {
        description: "Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-white border-slate-200" data-testid="application-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-800 flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: `${opportunity.color}15` }}
            >
              <opportunity.icon className="w-5 h-5" style={{ color: opportunity.color }} />
            </div>
            {opportunity.title}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Fill in your details to apply for this opportunity
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-slate-700">Full Name *</Label>
              <Input
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                placeholder="John Doe"
                className="bg-slate-50 border-slate-200 focus:border-[#2B7BB9] text-slate-800"
                data-testid="input-full-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-slate-700">Phone *</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="+91 9876543210"
                className="bg-slate-50 border-slate-200 focus:border-[#2B7BB9] text-slate-800"
                data-testid="input-phone"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-700">Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="john@example.com"
              className="bg-slate-50 border-slate-200 focus:border-[#2B7BB9] text-slate-800"
              data-testid="input-email"
            />
          </div>

          {(opportunity.id === "vendor" || opportunity.id === "logistics") && (
            <div className="space-y-2">
              <Label htmlFor="company_name" className="text-slate-700">Company/Business Name</Label>
              <Input
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Your Company Name"
                className="bg-slate-50 border-slate-200 focus:border-[#2B7BB9] text-slate-800"
                data-testid="input-company-name"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city" className="text-slate-700">City</Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Mumbai"
                className="bg-slate-50 border-slate-200 focus:border-[#2B7BB9] text-slate-800"
                data-testid="input-city"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state" className="text-slate-700">State</Label>
              <Input
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="Maharashtra"
                className="bg-slate-50 border-slate-200 focus:border-[#2B7BB9] text-slate-800"
                data-testid="input-state"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="experience" className="text-slate-700">
              {opportunity.id === "affiliate" ? "Platform/Channel Details" : "Experience/Background"}
            </Label>
            <Input
              id="experience"
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              placeholder={opportunity.id === "affiliate" ? "YouTube: 50K subscribers" : "5 years in FMCG"}
              className="bg-slate-50 border-slate-200 focus:border-[#2B7BB9] text-slate-800"
              data-testid="input-experience"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-slate-700">Additional Message</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us more about yourself..."
              rows={3}
              className="bg-slate-50 border-slate-200 focus:border-[#2B7BB9] text-slate-800 resize-none"
              data-testid="input-message"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-slate-300 text-slate-600 hover:bg-slate-100"
              data-testid="btn-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-[#FF9800] hover:bg-[#F57C00] text-white font-semibold"
              data-testid="btn-submit-application"
            >
              {isSubmitting ? "Submitting..." : "Submit Application"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Contact Form Component
const ContactForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully!", {
        description: "We'll get back to you soon.",
      });
      onClose();
      setFormData({
        full_name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to send message", {
        description: "Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-white border-slate-200" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-800 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#2B7BB9]/10">
              <Mail className="w-5 h-5 text-[#2B7BB9]" />
            </div>
            Contact Us
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Have questions? We'd love to hear from you.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact_name" className="text-slate-700">Full Name *</Label>
              <Input
                id="contact_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                placeholder="John Doe"
                className="bg-slate-50 border-slate-200 focus:border-[#2B7BB9] text-slate-800"
                data-testid="contact-input-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_phone" className="text-slate-700">Phone *</Label>
              <Input
                id="contact_phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="+91 9876543210"
                className="bg-slate-50 border-slate-200 focus:border-[#2B7BB9] text-slate-800"
                data-testid="contact-input-phone"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_email" className="text-slate-700">Email *</Label>
            <Input
              id="contact_email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="john@example.com"
              className="bg-slate-50 border-slate-200 focus:border-[#2B7BB9] text-slate-800"
              data-testid="contact-input-email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_subject" className="text-slate-700">Subject *</Label>
            <Input
              id="contact_subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              placeholder="Partnership Inquiry"
              className="bg-slate-50 border-slate-200 focus:border-[#2B7BB9] text-slate-800"
              data-testid="contact-input-subject"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_message" className="text-slate-700">Message *</Label>
            <Textarea
              id="contact_message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              placeholder="Tell us about your inquiry..."
              rows={4}
              className="bg-slate-50 border-slate-200 focus:border-[#2B7BB9] text-slate-800 resize-none"
              data-testid="contact-input-message"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-slate-300 text-slate-600 hover:bg-slate-100"
              data-testid="contact-btn-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-[#2B7BB9] hover:bg-[#236998] text-white font-semibold"
              data-testid="contact-btn-submit"
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Opportunity Card Component
const OpportunityCard = ({ opportunity, index, onApply }) => {
  return (
    <motion.div
      variants={fadeInUp}
      className="opportunity-card glass-card rounded-2xl overflow-hidden group"
      data-testid={`opportunity-card-${opportunity.id}`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={opportunity.image}
          alt={opportunity.title}
          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/30 to-transparent" />
        <div
          className="absolute top-4 left-4 w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
          style={{ background: `${opportunity.color}` }}
        >
          <opportunity.icon className="w-6 h-6 text-white" />
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-semibold text-slate-800 mb-1">{opportunity.title}</h3>
        <p className="text-sm text-[#4CAF50] font-medium mb-3">{opportunity.subtitle}</p>
        <p className="text-slate-600 text-sm mb-4">{opportunity.description}</p>

        {/* Benefits */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-slate-700 mb-2">Benefits:</h4>
          <ul className="space-y-1">
            {opportunity.benefits.slice(0, 3).map((benefit, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                <Check className="w-4 h-4 text-[#4CAF50] flex-shrink-0 mt-0.5" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Ideal For */}
        <div className="mb-5">
          <h4 className="text-sm font-medium text-slate-700 mb-2">Ideal For:</h4>
          <div className="flex flex-wrap gap-2">
            {opportunity.idealFor.slice(0, 3).map((item, i) => (
              <span
                key={i}
                className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-600 border border-slate-200"
              >
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Button
          onClick={() => onApply(opportunity)}
          className="w-full bg-[#FF9800] hover:bg-[#F57C00] text-white font-semibold rounded-full group"
          data-testid={`btn-apply-${opportunity.id}`}
        >
          {opportunity.cta}
          <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </motion.div>
  );
};

// WhatsApp Button Component
const WhatsAppButton = () => {
  const handleClick = () => {
    const message = encodeURIComponent("Hi! I'm interested in partnering with Dravina Group.");
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  return (
    <motion.button
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 1, type: "spring" }}
      onClick={handleClick}
      className="whatsapp-btn"
      data-testid="whatsapp-btn"
      aria-label="Contact on WhatsApp"
    >
      <MessageCircle className="w-7 h-7 text-white" fill="white" />
    </motion.button>
  );
};

// Mobile Navigation Component
const MobileNav = ({ isOpen, onClose, scrollToSection, openContact }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden"
          />
          {/* Menu */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25 }}
            className="fixed top-0 right-0 h-full w-72 bg-white shadow-2xl z-50 md:hidden"
            data-testid="mobile-nav"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-8">
                <img src={LOGO_URL} alt="Dravina" className="h-10 w-auto" />
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-slate-100"
                  data-testid="mobile-nav-close"
                >
                  <X className="w-6 h-6 text-slate-600" />
                </button>
              </div>
              <nav className="space-y-2">
                <button
                  onClick={() => { scrollToSection("opportunities"); onClose(); }}
                  className="w-full text-left px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
                  data-testid="mobile-nav-opportunities"
                >
                  Opportunities
                </button>
                <button
                  onClick={() => { scrollToSection("about"); onClose(); }}
                  className="w-full text-left px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
                  data-testid="mobile-nav-about"
                >
                  About
                </button>
                <button
                  onClick={() => { scrollToSection("contact"); onClose(); }}
                  className="w-full text-left px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
                  data-testid="mobile-nav-contact"
                >
                  Contact
                </button>
              </nav>
              <div className="mt-8 space-y-3">
                <Button
                  onClick={() => { scrollToSection("opportunities"); onClose(); }}
                  className="w-full bg-[#FF9800] hover:bg-[#F57C00] text-white font-semibold rounded-full"
                  data-testid="mobile-nav-apply"
                >
                  Apply Now
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { openContact(); onClose(); }}
                  className="w-full border-[#2B7BB9] text-[#2B7BB9] hover:bg-[#2B7BB9] hover:text-white rounded-full"
                  data-testid="mobile-nav-contact-btn"
                >
                  Contact Us
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Main App Component
function App() {
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-right" richColors />

      {/* Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-lg border-b border-slate-200/50 shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <img src={LOGO_URL} alt="Dravina Collective" className="h-12 w-auto" data-testid="nav-logo" />
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection("opportunities")} className="nav-link text-sm font-medium" data-testid="nav-opportunities">
                Opportunities
              </button>
              <button onClick={() => scrollToSection("about")} className="nav-link text-sm font-medium" data-testid="nav-about">
                About
              </button>
              <button onClick={() => scrollToSection("contact")} className="nav-link text-sm font-medium" data-testid="nav-contact">
                Contact
              </button>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => scrollToSection("opportunities")}
                className="hidden sm:flex bg-[#FF9800] hover:bg-[#F57C00] text-white font-semibold rounded-full"
                data-testid="nav-apply-btn"
              >
                Apply Now
              </Button>
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileNavOpen(true)}
                className="md:hidden p-2 rounded-lg hover:bg-slate-100"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-6 h-6 text-slate-700" />
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Navigation */}
      <MobileNav
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
        scrollToSection={scrollToSection}
        openContact={() => setIsContactOpen(true)}
      />

      {/* Hero Section */}
      <section className="hero-section pt-16" data-testid="hero-section">
        <div className="hero-bg" />
        <div className="hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-64px)] flex items-center">
          <div className="grid lg:grid-cols-2 gap-12 items-center py-20">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2B7BB9]/10 border border-[#2B7BB9]/30 mb-6">
                <Sparkles className="w-4 h-4 text-[#2B7BB9]" />
                <span className="text-sm text-[#2B7BB9] font-medium">Associate • Partner • Collaborate</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 mb-6 leading-tight">
                Grow With{" "}
                <span className="text-[#2B7BB9]">Dravina</span>
              </h1>
              
              <p className="text-xl text-slate-700 mb-4 font-medium">
                Let's Build Success Together
              </p>
              
              <p className="text-base text-slate-600 mb-8 max-w-xl">
                At Dravina Group, we believe in partnerships that create value, growth, and long-term opportunities. 
                Whether you are a business owner, professional, influencer, or entrepreneur, there are multiple ways 
                to collaborate and grow with us.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => scrollToSection("opportunities")}
                  className="bg-[#FF9800] hover:bg-[#F57C00] text-white font-semibold rounded-full px-8 py-6 text-base shadow-lg shadow-orange-200"
                  data-testid="hero-apply-btn"
                >
                  Apply Now
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsContactOpen(true)}
                  className="border-[#2B7BB9] text-[#2B7BB9] hover:bg-[#2B7BB9] hover:text-white rounded-full px-8 py-6 text-base"
                  data-testid="hero-contact-btn"
                >
                  Contact Us
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#2B7BB9]/10 to-[#4CAF50]/10 rounded-3xl blur-3xl" />
                <img
                  src={LOGO_URL}
                  alt="Dravina Collective"
                  className="relative w-full max-w-md mx-auto animate-float drop-shadow-2xl"
                  data-testid="hero-logo"
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 rounded-full border-2 border-slate-400 flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 rounded-full bg-[#2B7BB9]"
            />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-y border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {[
              { icon: Building2, value: "500+", label: "Partners" },
              { icon: Globe, value: "20+", label: "Cities" },
              { icon: Award, value: "10+", label: "Years Experience" },
              { icon: Target, value: "1000+", label: "Products" },
            ].map((stat, i) => (
              <motion.div key={i} variants={fadeInUp} className="text-center">
                <div className="w-12 h-12 rounded-xl bg-[#2B7BB9]/10 flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="w-6 h-6 text-[#2B7BB9]" />
                </div>
                <div className="text-3xl font-bold text-slate-800 mb-1">{stat.value}</div>
                <div className="text-sm text-slate-600">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Opportunities Section */}
      <section id="opportunities" className="py-24 bg-white" data-testid="opportunities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Collaboration Opportunities
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Explore the opportunities below and join our growing ecosystem
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {opportunities.map((opp, index) => (
              <OpportunityCard
                key={opp.id}
                opportunity={opp}
                index={index}
                onApply={setSelectedOpportunity}
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-slate-50" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
                Why Partner With Dravina?
              </h2>
              <p className="text-slate-600 mb-6">
                Dravina Group is building a strong ecosystem of partners, professionals, and entrepreneurs across India. 
                Join us today and become part of a fast-growing business network.
              </p>
              <ul className="space-y-4">
                {[
                  "Pan-India distribution network",
                  "Professional training & support",
                  "Transparent earning opportunities",
                  "Long-term growth partnership",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <div className="w-6 h-6 rounded-full bg-[#4CAF50]/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-[#4CAF50]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="glass-card rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-slate-800 mb-6">Quick Contact</h3>
                <div className="space-y-4">
                  <a
                    href={`mailto:hr@dravina.in`}
                    className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors border border-slate-200"
                    data-testid="quick-contact-email"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#2B7BB9]/10 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[#2B7BB9]" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-500">Email</div>
                      <div className="text-slate-800 font-medium">hr@dravina.in</div>
                    </div>
                  </a>
                  <a
                    href={`tel:${WHATSAPP_NUMBER}`}
                    className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors border border-slate-200"
                    data-testid="quick-contact-phone"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#4CAF50]/10 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-[#4CAF50]" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-500">Phone</div>
                      <div className="text-slate-800 font-medium">+91-9030636164</div>
                    </div>
                  </a>
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <div className="w-10 h-10 rounded-lg bg-[#FF9800]/10 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#FF9800]" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-500">Location</div>
                      <div className="text-slate-800 font-medium">Pan India Operations</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-[#2B7BB9] to-[#1e6091]" data-testid="contact-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Let's Grow Together
            </h2>
            <p className="text-base text-blue-100 mb-8 max-w-2xl mx-auto">
              Ready to start your partnership journey with Dravina? Contact us today and 
              let's discuss how we can grow together.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                onClick={() => setIsContactOpen(true)}
                className="bg-white hover:bg-slate-100 text-[#2B7BB9] font-semibold rounded-full px-8 py-6 text-base shadow-lg"
                data-testid="cta-contact-btn"
              >
                <Send className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
              <Button
                onClick={() => {
                  const message = encodeURIComponent("Hi! I'm interested in partnering with Dravina Group.");
                  window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
                }}
                className="bg-[#25D366] hover:bg-[#1fba59] text-white font-semibold rounded-full px-8 py-6 text-base shadow-lg"
                data-testid="cta-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5 mr-2" fill="white" />
                WhatsApp
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-200 bg-white" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <img src={LOGO_URL} alt="Dravina Collective" className="h-12 w-auto mb-4" />
              <p className="text-sm text-slate-600">
                Building partnerships that create value, growth, and long-term opportunities across India.
              </p>
            </div>
            <div>
              <h4 className="text-slate-800 font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection("opportunities")} className="footer-link text-sm">Opportunities</button></li>
                <li><button onClick={() => scrollToSection("about")} className="footer-link text-sm">About Us</button></li>
                <li><button onClick={() => setIsContactOpen(true)} className="footer-link text-sm">Contact</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-slate-800 font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>Email: hr@dravina.in</li>
                <li>Phone: +91-9030636164</li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} Dravina Collective. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <WhatsAppButton />

      {/* Application Form Modal */}
      {selectedOpportunity && (
        <ApplicationForm
          opportunity={selectedOpportunity}
          isOpen={!!selectedOpportunity}
          onClose={() => setSelectedOpportunity(null)}
        />
      )}

      {/* Contact Form Modal */}
      <ContactForm isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </div>
  );
}

export default App;
